import axios from 'axios';
import { firstNonEmpty } from 'utils/errors';
import { AssociationOutcome, RuleViolation, TenantValidation } from './types';

// The accept call answers in three structurally different ways and they do not
// share a path. jarvis-api wraps everything in `{ statusCode, message, body }`,
// where `body` is MAPI's own envelope - so a 202's detail sits at `body.tenants`
// while a 400's sits one level deeper, at `body.data.tenants` / `body.data.errors`.
// Normalising here is deliberate: jarvis-api's response shapes are frozen under
// their own ticket, so the client is the only place that can reconcile them.
interface MapiErrorEnvelope {
  data?: {
    batchId?: string;
    errors?: unknown;
    tenants?: unknown;
    validCount?: number;
    invalidCount?: number;
    message?: string;
  };
  message?: string;
}

interface JarvisEnvelope {
  message?: string;
  body?: MapiErrorEnvelope & {
    batchId?: string;
    validCount?: number;
    invalidCount?: number;
    tenants?: unknown;
  };
}

// DTO-level failures come back as plain strings rather than { rule, message }
// objects, so both shapes have to survive the same reader.
const toRuleViolations = (raw: unknown): RuleViolation[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(entry => {
      if (typeof entry === 'string') return { rule: '', message: entry };
      const { rule, message } = (entry ?? {}) as Partial<RuleViolation>;
      return { rule: rule ?? '', message: message ?? '' };
    })
    .filter(({ message }) => message !== '');
};

const toTenantValidations = (raw: unknown): TenantValidation[] => {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap(entry => {
    const { tenantId, status, validationErrors } = (entry ??
      {}) as Partial<TenantValidation>;
    if (!tenantId) return [];
    return [
      {
        tenantId,
        status: status === 'valid' ? 'valid' : 'invalid',
        validationErrors: toRuleViolations(validationErrors)
      } as TenantValidation
    ];
  });
};

/** The 202 body: the batch started, though some tenants may have been skipped. */
export const parseAcceptedResponse = (res: unknown): AssociationOutcome => {
  const body = (res as JarvisEnvelope)?.body ?? {};
  const tenants = toTenantValidations(body.tenants);
  const invalid = tenants.filter(({ status }) => status === 'invalid');

  return {
    kind: 'accepted',
    batchId: body.batchId,
    // Trust the counts MAPI sent; fall back to the list when they are absent.
    validCount: body.validCount ?? tenants.length - invalid.length,
    // Same reasoning as the rejected branch: the number has to match the rows.
    invalidCount: Math.min(body.invalidCount ?? invalid.length, invalid.length),
    tenants
  };
};

/** A thrown request: either of the two 400 shapes, or something unreadable. */
export const parseAssociationError = (err: unknown): AssociationOutcome => {
  const envelope = axios.isAxiosError<JarvisEnvelope>(err)
    ? err.response?.data
    : undefined;
  const mapi = envelope?.body;
  const detail = mapi?.data;

  const errors = toRuleViolations(detail?.errors);
  if (errors.length > 0) {
    return { kind: 'upfront-rejected', batchId: detail?.batchId, errors };
  }

  const tenants = toTenantValidations(detail?.tenants);
  if (tenants.length > 0) {
    return {
      kind: 'tenants-rejected',
      batchId: detail?.batchId,
      // Never claim more than the list can account for: a malformed entry is
      // dropped above, and a count from MAPI that outruns the list would render
      // "3 Skipped" over two rows.
      invalidCount: Math.min(
        detail?.invalidCount ?? tenants.length,
        tenants.length
      ),
      tenants
    };
  }

  // No structured detail. `envelope.message` is jarvis-api's copy of the upstream
  // HTTP status text ("Bad Request"), so it is the last resort, not the first.
  return {
    kind: 'failed',
    message: firstNonEmpty(mapi?.message, detail?.message, envelope?.message)
  };
};
